import { Instance, SnapshotIn, types, flow, getEnv, cast } from 'mobx-state-tree';

import { IStoresEnv } from '@core/storesEnv';
import { ExecutionsStore } from './Executions.store';
import { PageQueryStore } from './PageQuery.store';
import { PaginationStatusStore } from './PaginationStatus.store';

import { performGetExecutions, performGetExecutionsFullData } from 'NeoAnalytics/services';
import { ExecutionsFiltersOptionsStore } from './ExecutionsFiltersOptions.store';

function formatLocalDateTime(value: Date): string {
    const pad = (part: number) => String(part).padStart(2, '0');

    return [
        `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
        `${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`,
    ].join('T');
}

function normalizeDateParam(value: unknown): string | undefined {
    if (value == null || value === '') {
        return undefined;
    }

    if (value instanceof Date) {
        return formatLocalDateTime(value);
    }

    if (typeof value === 'number' || typeof value === 'string') {
        const nextDate = new Date(value);

        if (!Number.isNaN(nextDate.getTime())) {
            return formatLocalDateTime(nextDate);
        }
    }

    return undefined;
}

function getExecutionQueryPayload(params: Record<string, any>) {
    const from = normalizeDateParam(params.from);
    const to = normalizeDateParam(params.to);

    return {
        ...params,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
    };
}

export const ExecutionsGridStoreInferred = types
    .model('ExecutionsGridStoreInferred', {
        executionsList: types.array(ExecutionsStore),
        requestParams: types.optional(PageQueryStore, {}),
        paginationStatus: types.optional(PaginationStatusStore, {}),
        executionsFilterOptions: types.optional(ExecutionsFiltersOptionsStore, {}),
    })
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);
        let latestLoadRequestId = 0;

        const loadExecutions = flow(function* () {
            const requestId = ++latestLoadRequestId;
            const payload = getExecutionQueryPayload(self.requestParams.getParams);

            if(self.requestParams.getParams.fullData){
                const result = yield performGetExecutionsFullData(
                    api,
                    {
                        payload,
                        errorHandlers: { DEFAULT: '' },
                    },
                );

                if(result.success){
                    if (requestId !== latestLoadRequestId) {
                        return;
                    }
                    self.executionsList = result.data;
                    self.paginationStatus = result.data.meta;
                }
            }else{
                const result = yield performGetExecutions(
                    api,
                    {
                        payload,
                        errorHandlers: { DEFAULT: '' },
                    },
                );

                if(result.success){
                    if (requestId !== latestLoadRequestId) {
                        return;
                    }
                    self.executionsList = result.data;
                    self.paginationStatus = result.data.meta;
                }
            }
        });

        const cleanData = () => {
            latestLoadRequestId += 1;
            self.executionsList = cast([]);
            self.paginationStatus = cast({});
        };

        return {
            loadExecutions,
            cleanData,
        }
    })

type ExecutionsGridStoreFactoryType = typeof ExecutionsGridStoreInferred;
interface IExecutionsGridStoreFactory extends ExecutionsGridStoreFactoryType { }
export const ExecutionsGridStore: IExecutionsGridStoreFactory = ExecutionsGridStoreInferred;
export interface IExecutionsGridStore extends Instance<IExecutionsGridStoreFactory> { }
export interface IExecutionsGridStoreSnapshotIn extends SnapshotIn<IExecutionsGridStore> { }
